package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import kotlinx.coroutines.launch
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.foundation.BorderStroke
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HistorialPagosView(viewModel: AdminViewModel) {
    val pagos by viewModel.historialPagos.collectAsState()
    val cargando by viewModel.cargandoPagos.collectAsState()
    var busqueda by remember { mutableStateOf("") }
    var filtroMetodo by remember { mutableStateOf("Todos") }
    var filtroStatus by remember { mutableStateOf("Todos") }
    var fechaDesde by remember { mutableStateOf("") }
    var fechaHasta by remember { mutableStateOf("") }
    var expandedMetodo by remember { mutableStateOf(false) }
    var expandedStatus by remember { mutableStateOf(false) }
    var pagoSeleccionado by remember { mutableStateOf<PagoRegistro?>(null) }
    val clipboardManager = LocalClipboardManager.current

    // Carga inicial (usa cache si <6h) + auto-refresh cada 6h mientras la pantalla esté visible.
    LaunchedEffect(Unit) {
        viewModel.cargarHistorialPagos()
        while (true) {
            kotlinx.coroutines.delay(6L * 60L * 60L * 1000L)  // 6 horas
            viewModel.cargarHistorialPagos(force = true)
        }
    }

    val hayFiltroActivo = busqueda.isNotBlank() || filtroMetodo != "Todos" || filtroStatus != "Todos" ||
        fechaDesde.isNotBlank() || fechaHasta.isNotBlank()

    val pagosFiltrados = pagos.filter { pago ->
        val matchBusqueda = busqueda.isBlank() ||
            pago.paymentIntentId.contains(busqueda, ignoreCase = true) ||
            pago.email.contains(busqueda, ignoreCase = true) ||
            pago.clabe.contains(busqueda, ignoreCase = true) ||
            pago.oxxoNumber.contains(busqueda, ignoreCase = true) ||
            pago.ruta.contains(busqueda, ignoreCase = true) ||
            pago.pasajeros.any { it.nombre.contains(busqueda, ignoreCase = true) }
        val matchMetodo = filtroMetodo == "Todos" || pago.metodoPago.equals(filtroMetodo, ignoreCase = true)
        val matchStatus = filtroStatus == "Todos" || pago.status.equals(filtroStatus, ignoreCase = true)
        val matchFecha = (fechaDesde.isBlank() || pago.fecha >= fechaDesde) &&
            (fechaHasta.isBlank() || pago.fecha <= fechaHasta)
        matchBusqueda && matchMetodo && matchStatus && matchFecha
    }

    // Sin filtros: mostrar los últimos 20 (ordenados desc por repo). Con filtros: mostrar todos.
    val pagosMostrados = if (hayFiltroActivo) pagosFiltrados else pagosFiltrados.take(20)
    // Total de ingresos del periodo/filtro actual (solo pagos confirmados)
    val totalIngresos = pagosFiltrados.filter { it.status == "confirmado" }.sumOf { it.monto }

    val listState = androidx.compose.foundation.lazy.rememberLazyListState()
    val scope = rememberCoroutineScope()
    val confirmados = pagosFiltrados.count { it.status == "confirmado" }
    val pendientes = pagosFiltrados.count { it.status == "pendiente" }
    val conflictos = pagosFiltrados.count { it.status == "conflicto" }
    val expirados = pagosFiltrados.count { it.status == "expirado" }

    Box(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            state = listState,
            modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(top = 16.dp, bottom = 88.dp)
        ) {
            // Título + refrescar (se desplaza con el contenido para dar más espacio a la lista)
            item {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                    Icon(Icons.Filled.Receipt, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(28.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Historial de Pagos", style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                    Spacer(modifier = Modifier.weight(1f))
                    IconButton(onClick = { viewModel.cargarHistorialPagos(force = true) }) {
                        Icon(Icons.Filled.Refresh, contentDescription = "Actualizar", tint = SmartVanColors.CyanAccent)
                    }
                }
            }

            // Barra de búsqueda
            item {
                OutlinedTextField(
                    value = busqueda,
                    onValueChange = { busqueda = it },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = { Text("Buscar por referencia, correo, CLABE, pasajero...") },
                    leadingIcon = { Icon(Icons.Filled.Search, contentDescription = null, tint = Color.Gray) },
                    shape = RoundedCornerShape(12.dp),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = SmartVanColors.AzulOscuro)
                )
            }

            // Botones rápidos de estado (pendiente / confirmado / etc.) — filtran al instante.
            item {
                CarruselHorizontal(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(listOf(
                        "Todos" to "Todos",
                        "Pendientes" to "pendiente",
                        "Confirmados" to "confirmado",
                        "Conflictos" to "conflicto",
                        "Expirados" to "expirado"
                    )) { (label, valor) ->
                        FilterChip(
                            selected = filtroStatus == valor,
                            onClick = { filtroStatus = valor },
                            label = { Text(label, fontSize = 12.sp, maxLines = 1) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = SmartVanColors.AzulOscuro,
                                selectedLabelColor = Color.White
                            )
                        )
                    }
                }
            }

            // Filtro por método (dropdown) + rango de fecha
            item {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    ExposedDropdownMenuBox(expanded = expandedMetodo, onExpandedChange = { expandedMetodo = it }, modifier = Modifier.weight(1f)) {
                        OutlinedTextField(
                            value = if (filtroMetodo == "Todos") "Método: Todos" else filtroMetodo.uppercase().replace("_", " "),
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Método", fontSize = 11.sp) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedMetodo) },
                            modifier = Modifier.menuAnchor().fillMaxWidth(),
                            textStyle = MaterialTheme.typography.bodySmall,
                            shape = RoundedCornerShape(10.dp)
                        )
                        ExposedDropdownMenu(expanded = expandedMetodo, onDismissRequest = { expandedMetodo = false }) {
                            listOf("Todos", "spei", "oxxo", "transferencia_manual", "efectivo", "card").forEach { opcion ->
                                DropdownMenuItem(text = { Text(opcion.uppercase().replace("_", " ")) }, onClick = { filtroMetodo = opcion; expandedMetodo = false })
                            }
                        }
                    }
                }
            }

            // Rango de fecha (AAAA-MM-DD)
            item {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = fechaDesde,
                        onValueChange = { fechaDesde = it.filter { c -> c.isDigit() || c == '-' }.take(10) },
                        label = { Text("Desde (AAAA-MM-DD)", fontSize = 10.sp) },
                        modifier = Modifier.weight(1f),
                        singleLine = true,
                        textStyle = MaterialTheme.typography.bodySmall,
                        shape = RoundedCornerShape(10.dp)
                    )
                    OutlinedTextField(
                        value = fechaHasta,
                        onValueChange = { fechaHasta = it.filter { c -> c.isDigit() || c == '-' }.take(10) },
                        label = { Text("Hasta (AAAA-MM-DD)", fontSize = 10.sp) },
                        modifier = Modifier.weight(1f),
                        singleLine = true,
                        textStyle = MaterialTheme.typography.bodySmall,
                        shape = RoundedCornerShape(10.dp)
                    )
                }
            }

            // Total de ingresos confirmados
            item {
                Card(
                    colors = CardDefaults.cardColors(containerColor = SmartVanColors.Verde.copy(alpha = 0.12f)),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.Payments, contentDescription = null, tint = SmartVanColors.VerdeOscuro, modifier = Modifier.size(20.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Ingresos confirmados del periodo", color = SmartVanColors.VerdeOscuro, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                        Spacer(modifier = Modifier.weight(1f))
                        Text("$$totalIngresos MXN", color = SmartVanColors.VerdeOscuro, fontWeight = FontWeight.Black)
                    }
                }
            }

            // Chips resumen — clicables: tocar filtra por ese estado (tocar de nuevo = Todos).
            item {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    PagoResumenChip("Confirmados", confirmados, SmartVanColors.Verde,
                        Modifier.weight(1f).clickable { filtroStatus = if (filtroStatus == "confirmado") "Todos" else "confirmado" })
                    PagoResumenChip("Pendientes", pendientes, SmartVanColors.Amarillo,
                        Modifier.weight(1f).clickable { filtroStatus = if (filtroStatus == "pendiente") "Todos" else "pendiente" })
                    PagoResumenChip("Conflictos", conflictos, Color.Red,
                        Modifier.weight(1f).clickable { filtroStatus = if (filtroStatus == "conflicto") "Todos" else "conflicto" })
                    PagoResumenChip("Expirados", expirados, Color.Gray,
                        Modifier.weight(1f).clickable { filtroStatus = if (filtroStatus == "expirado") "Todos" else "expirado" })
                }
            }

            // Estados: cargando / vacío / hint de "últimos 20"
            if (cargando) {
                item {
                    Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
                    }
                }
            } else if (pagosMostrados.isEmpty()) {
                item {
                    Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                        Text(
                            if (hayFiltroActivo) "No se encontraron pagos con esos filtros."
                            else "Aún no hay pagos registrados.",
                            color = Color.Gray, fontStyle = FontStyle.Italic
                        )
                    }
                }
            } else {
                if (!hayFiltroActivo && pagosFiltrados.size > 20) {
                    item {
                        Text(
                            "Mostrando los últimos 20 de ${pagosFiltrados.size}. Usa filtros para ver más.",
                            color = Color.Gray, fontStyle = FontStyle.Italic,
                            style = MaterialTheme.typography.labelSmall
                        )
                    }
                }
                items(pagosMostrados, key = { it.paymentIntentId }) { pago ->
                    PagoCard(pago = pago, onClick = { pagoSeleccionado = pago }, onCopyRef = { clipboardManager.setText(AnnotatedString(pago.paymentIntentId)) })
                }
            }
        }

        // Botón flotante "subir al inicio": aparece cuando ya bajó de la primera pantalla.
        val mostrarSubir by remember { derivedStateOf { listState.firstVisibleItemIndex > 3 } }
        if (mostrarSubir) {
            FloatingActionButton(
                onClick = { scope.launch { listState.animateScrollToItem(0) } },
                modifier = Modifier.align(Alignment.BottomEnd).padding(20.dp),
                containerColor = SmartVanColors.AzulOscuro,
                contentColor = Color.White
            ) {
                Icon(Icons.Filled.KeyboardArrowUp, contentDescription = "Subir al inicio")
            }
        }
    }

    // Detalle del pago seleccionado
    if (pagoSeleccionado != null) {
        val pago = pagoSeleccionado!!
        AlertDialog(
            onDismissRequest = { pagoSeleccionado = null },
            title = {
                Column {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        val statusIcon = when (pago.status) { "confirmado" -> Icons.Filled.CheckCircle; "conflicto" -> Icons.Filled.Warning; "pendiente" -> Icons.Filled.HourglassEmpty; else -> Icons.Filled.Timer }
                        val statusColor = when (pago.status) { "confirmado" -> SmartVanColors.Verde; "conflicto" -> Color.Red; "pendiente" -> SmartVanColors.Amarillo; else -> Color.Gray }
                        Icon(statusIcon, contentDescription = null, tint = statusColor, modifier = Modifier.size(24.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(pago.status.uppercase(), fontWeight = FontWeight.Black, color = statusColor)
                        Spacer(modifier = Modifier.weight(1f))
                        Text("$${pago.monto} MXN", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                    }
                    Text(pago.metodoPago.uppercase(), color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                }
            },
            text = {
                Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                    // Referencia
                    Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFF5F7FF)), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text("Referencia Stripe", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.labelSmall)
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Text(pago.paymentIntentId, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                                IconButton(onClick = { clipboardManager.setText(AnnotatedString(pago.paymentIntentId)) }, modifier = Modifier.size(28.dp)) {
                                    Icon(Icons.Filled.ContentCopy, contentDescription = "Copiar", tint = SmartVanColors.CyanAccent, modifier = Modifier.size(16.dp))
                                }
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(8.dp))

                    // CLABE o Número Oxxo
                    if (pago.clabe.isNotBlank()) {
                        Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFE3F2FD)), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                Text("CLABE SPEI", fontWeight = FontWeight.Bold, color = Color(0xFF1565C0), style = MaterialTheme.typography.labelSmall)
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(pago.clabe, color = Color(0xFF1565C0), fontWeight = FontWeight.Medium, style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                                    IconButton(onClick = { clipboardManager.setText(AnnotatedString(pago.clabe)) }, modifier = Modifier.size(28.dp)) {
                                        Icon(Icons.Filled.ContentCopy, contentDescription = "Copiar", tint = Color(0xFF1565C0), modifier = Modifier.size(16.dp))
                                    }
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                    }
                    if (pago.oxxoNumber.isNotBlank()) {
                        Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoNaranja), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                Text("Número OXXO", fontWeight = FontWeight.Bold, color = SmartVanColors.NaranjaDeep, style = MaterialTheme.typography.labelSmall)
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(pago.oxxoNumber, color = SmartVanColors.NaranjaDeep, fontWeight = FontWeight.Medium, style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                                    IconButton(onClick = { clipboardManager.setText(AnnotatedString(pago.oxxoNumber)) }, modifier = Modifier.size(28.dp)) {
                                        Icon(Icons.Filled.ContentCopy, contentDescription = "Copiar", tint = SmartVanColors.NaranjaDeep, modifier = Modifier.size(16.dp))
                                    }
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                    }

                    // Info del viaje
                    Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.Map, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text(pago.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro) }
                            Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.CalendarToday, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text("${pago.fecha} • ${pago.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall) }
                            if (pago.asientos.isNotBlank()) { Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.EventSeat, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text("Asientos: ${pago.asientos}", color = Color.Gray, style = MaterialTheme.typography.bodySmall) } }
                            if (pago.email.isNotBlank()) { Row(verticalAlignment = Alignment.CenterVertically) { Icon(Icons.Filled.Email, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp)); Spacer(modifier = Modifier.width(6.dp)); Text(pago.email, color = Color.Gray, style = MaterialTheme.typography.bodySmall) } }
                        }
                    }
                    Spacer(modifier = Modifier.height(8.dp))

                    // Pasajeros
                    if (pago.pasajeros.isNotEmpty()) {
                        Text("Pasajeros", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.labelMedium)
                        Spacer(modifier = Modifier.height(4.dp))
                        pago.pasajeros.forEach { p ->
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(vertical = 2.dp)) {
                                Icon(Icons.Filled.Person, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(14.dp))
                                Spacer(modifier = Modifier.width(6.dp))
                                Text("${p.nombre} — Asiento ${p.asiento}", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                            }
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                    }

                    // Timestamp
                    if (pago.timestamp > 0L) {
                        Text("Registrado: ${org.example.abigail.formatMillisToDate(pago.timestamp)}", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                    }

                    // Motivo si es conflicto
                    if (pago.motivo.isNotBlank()) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo), shape = RoundedCornerShape(8.dp), modifier = Modifier.fillMaxWidth()) {
                            Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.Warning, contentDescription = null, tint = Color.Red, modifier = Modifier.size(16.dp))
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(pago.motivo, color = Color.Red, fontWeight = FontWeight.Medium, style = MaterialTheme.typography.bodySmall)
                            }
                        }
                    }
                }
            },
            confirmButton = { TextButton(onClick = { pagoSeleccionado = null }) { Text("Cerrar", color = SmartVanColors.AzulOscuro) } }
        )
    }
}

@Composable
fun PagoResumenChip(label: String, count: Int, color: Color, modifier: Modifier = Modifier) {
    Card(
        colors = CardDefaults.cardColors(containerColor = color.copy(alpha = 0.1f)),
        shape = RoundedCornerShape(10.dp),
        modifier = modifier
    ) {
        Column(modifier = Modifier.padding(8.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text("$count", fontWeight = FontWeight.Black, color = color, fontSize = 18.sp)
            Text(label, color = color, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Medium)
        }
    }
}

@Composable
fun PagoCard(pago: PagoRegistro, onClick: () -> Unit, onCopyRef: () -> Unit) {
    val statusColor = when (pago.status) { "confirmado" -> SmartVanColors.Verde; "conflicto" -> Color.Red; "pendiente" -> SmartVanColors.Amarillo; else -> Color.Gray }
    val statusIcon = when (pago.status) { "confirmado" -> Icons.Filled.CheckCircle; "conflicto" -> Icons.Filled.Warning; "pendiente" -> Icons.Filled.HourglassEmpty; else -> Icons.Filled.Timer }
    val metodoBadgeColor = when (pago.metodoPago.lowercase()) { "spei" -> Color(0xFF1565C0); "oxxo" -> SmartVanColors.NaranjaDeep; "tarjeta" -> SmartVanColors.PurpuraDark; "efectivo" -> SmartVanColors.VerdeOscuro; else -> Color.Gray }

    Card(
        modifier = Modifier.fillMaxWidth().clickable { onClick() },
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(1.dp, statusColor.copy(alpha = 0.3f)),
        colors = CardDefaults.cardColors(containerColor = Color.White)
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                Icon(statusIcon, contentDescription = null, tint = statusColor, modifier = Modifier.size(18.dp))
                Spacer(modifier = Modifier.width(6.dp))
                Text(pago.status.uppercase(), fontWeight = FontWeight.Bold, color = statusColor, style = MaterialTheme.typography.labelSmall)
                Spacer(modifier = Modifier.width(8.dp))
                Box(modifier = Modifier.background(metodoBadgeColor.copy(alpha = 0.15f), RoundedCornerShape(20.dp)).padding(horizontal = 8.dp, vertical = 2.dp)) {
                    Text(pago.metodoPago.uppercase(), fontWeight = FontWeight.Bold, color = metodoBadgeColor, fontSize = 10.sp)
                }
                Spacer(modifier = Modifier.weight(1f))
                Text("$${pago.monto} MXN", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            }
            Spacer(modifier = Modifier.height(6.dp))
            Text(pago.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium)
            Text("${pago.fecha} • ${pago.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
            if (pago.pasajeros.isNotEmpty()) {
                // 3+ pasajeros con nombres largos se cortaban por maxLines=1 sin elipsis.
                Text(pago.pasajeros.joinToString(", ") { it.nombre }, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall, maxLines = 2, overflow = TextOverflow.Ellipsis)
            }
            Spacer(modifier = Modifier.height(4.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(if (pago.paymentIntentId.length > 20) pago.paymentIntentId.take(20) + "..." else pago.paymentIntentId, color = Color.Gray, style = MaterialTheme.typography.labelSmall, modifier = Modifier.weight(1f))
                IconButton(onClick = onCopyRef, modifier = Modifier.size(24.dp)) {
                    Icon(Icons.Filled.ContentCopy, contentDescription = "Copiar referencia", tint = SmartVanColors.CyanAccent, modifier = Modifier.size(14.dp))
                }
            }
            if (pago.requiereAtencion) {
                Spacer(modifier = Modifier.height(4.dp))
                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo), shape = RoundedCornerShape(6.dp)) {
                    Row(modifier = Modifier.padding(6.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.Warning, contentDescription = null, tint = Color.Red, modifier = Modifier.size(12.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Requiere atención manual", color = Color.Red, fontSize = 10.sp, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}
